import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Bell, Save, RefreshCw, Phone, Mail, AlertTriangle, Award, GraduationCap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DIVISIONS = [
  { key: "mariners", label: "Mariners", color: "indigo" },
  { key: "skippers", label: "Skippers", color: "amber" },
  { key: "navigators", label: "Navigators", color: "violet" },
];
const ROLES = [
  { key: "supervisor", label: "Supervisor" },
  { key: "assistant", label: "Assistant" },
];

export default function DivisionNotificationsView({ onBack }) {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const [pool, setPool] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Specialist recipients (2 people)
  const [specialistRecipients, setSpecialistRecipients] = useState([null, null]); // [pid, pid]
  const [specialistDirty, setSpecialistDirty] = useState(false);
  const [specialistSaving, setSpecialistSaving] = useState(false);
  // Skippers group leaders (2 roles)
  const [skipperLeaders, setSkipperLeaders] = useState([]); // [{leader_role,label,person_id,group_codes,...}]
  const [skipperLeadersDirty, setSkipperLeadersDirty] = useState(false);
  const [skipperLeadersSaving, setSkipperLeadersSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, aRes, sRes, lRes] = await Promise.all([
        axios.get(`${API}/admin/division-notifications/pool`, { headers }),
        axios.get(`${API}/admin/division-notifications/assignments`, { headers }),
        axios.get(`${API}/admin/specialist-recipients`, { headers }),
        axios.get(`${API}/admin/skippers-group-leaders`, { headers }),
      ]);
      setPool(pRes.data || []);
      setAssignments(aRes.data || []);
      const sRecipients = sRes.data?.recipients || [];
      setSpecialistRecipients([
        sRecipients[0]?.person_id ?? null,
        sRecipients[1]?.person_id ?? null,
      ]);
      setSkipperLeaders(lRes.data || []);
      setDirty(false);
      setSpecialistDirty(false);
      setSkipperLeadersDirty(false);
    } catch {
      toast.error("Failed to load assignments");
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/division-notifications/pool/sync`, {}, { headers });
      toast.success(`Synced ${res.data.imported} supervisor${res.data.imported !== 1 ? "s" : ""} from CampMinder`);
      await load();
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const updateSlot = (division, role, personIdStr) => {
    const pid = personIdStr === "" ? null : Number(personIdStr);
    setAssignments(prev => prev.map(a =>
      a.division === division && a.role === role
        ? { ...a, person_id: pid, name: pid ? (pool.find(p => p.person_id === pid)?.name || "") : "", phone: pid ? (pool.find(p => p.person_id === pid)?.phone || "") : "" }
        : a
    ));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        assignments: assignments.map(a => ({
          division: a.division, role: a.role, person_id: a.person_id,
        })),
      };
      await axios.put(`${API}/admin/division-notifications/assignments`, body, { headers });
      toast.success("Notification assignments saved");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const getSlot = (div, role) =>
    assignments.find(a => a.division === div && a.role === role) || { person_id: null, name: "", phone: "" };

  // ── Specialist recipients ──
  const updateSpecialistSlot = (idx, personIdStr) => {
    const pid = personIdStr === "" ? null : Number(personIdStr);
    setSpecialistRecipients(prev => {
      const next = [...prev];
      next[idx] = pid;
      return next;
    });
    setSpecialistDirty(true);
  };
  const saveSpecialistRecipients = async () => {
    setSpecialistSaving(true);
    try {
      const ids = specialistRecipients.filter(x => x != null);
      await axios.put(`${API}/admin/specialist-recipients`, { person_ids: ids }, { headers });
      toast.success("Specialist recipients saved");
      setSpecialistDirty(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSpecialistSaving(false);
    }
  };

  // ── Skippers group leaders ──
  const updateSkipperLeader = (roleKey, personIdStr) => {
    const pid = personIdStr === "" ? null : Number(personIdStr);
    setSkipperLeaders(prev => prev.map(l =>
      l.leader_role === roleKey
        ? { ...l, person_id: pid }
        : l
    ));
    setSkipperLeadersDirty(true);
  };
  const saveSkipperLeaders = async () => {
    setSkipperLeadersSaving(true);
    try {
      const body = {
        leaders: skipperLeaders.map(l => ({
          leader_role: l.leader_role,
          person_id: l.person_id,
          group_codes: l.group_codes,
        })),
      };
      await axios.put(`${API}/admin/skippers-group-leaders`, body, { headers });
      toast.success("Skippers group leaders saved");
      setSkipperLeadersDirty(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSkipperLeadersSaving(false);
    }
  };

  const findPoolPerson = (pid) => pool.find(p => p.person_id === pid);
  // Scoped pools — narrowed by CampMinder position category (set on sync).
  // Supervisors/Directors-only for the supervisor + assistant + specialist slots;
  // only the 2 Group Leaders for the Skippers leader slots.
  const supervisorPool = pool.filter(p => p.category === "supervisor");
  const groupLeaderPool = pool.filter(p => p.category === "group_leader");
  // If the currently-assigned person sits outside the scoped pool (e.g. an
  // earlier admin picked someone not flagged as Supervisor), still show them so
  // the slot doesn't reset to "Not assigned" on render.
  const ensureIncluded = (scoped, pid) => {
    if (pid && !scoped.find(p => p.person_id === pid)) {
      const extra = findPoolPerson(pid);
      if (extra) return [extra, ...scoped];
    }
    return scoped;
  };

  return (
    <div className="space-y-4" data-testid="division-notifications-view">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="division-notifications-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h2 className="font-heading font-bold text-lg flex items-center gap-2">
          <Bell className="w-5 h-5 text-rose-600" />Division Incident Notifications
        </h2>
      </div>

      <Card className="border-rose-200 bg-rose-50/40">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm space-y-1">
              <p className="font-semibold text-rose-900">How this works</p>
              <p className="text-rose-800">
                When a counselor files an Incident Report, the assigned Supervisor <em>and</em> Assistant for that division will each receive an SMS:
              </p>
              <p className="font-mono text-xs bg-white border border-rose-200 rounded px-2 py-1 inline-block mt-1">
                "You have a new incident report for group B08 Behavioral."
              </p>
              <p className="text-rose-700 text-xs">Great-day reports do NOT trigger notifications. Includes the standard opt-out footer.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between items-center">
        <p className="text-xs text-muted-foreground">
          Supervisor/Assistant + Specialist slots show only CampMinder Supervisors/Directors ({supervisorPool.length}). Skippers Group Leader slots show only CampMinder Group Leaders ({groupLeaderPool.length}).
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={handleSync}
          disabled={syncing}
          data-testid="division-notifications-sync"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing..." : "Re-sync from CampMinder"}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-muted-foreground">Loading...</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-3">
          {DIVISIONS.map(div => (
            <Card key={div.key} className="overflow-hidden">
              <div className={`h-2 bg-${div.color}-500`} />
              <CardContent className="p-4 space-y-3">
                <h3 className={`font-bold text-${div.color}-800 flex items-center gap-2`}>
                  {div.label}
                </h3>
                {ROLES.map(role => {
                  const slot = getSlot(div.key, role.key);
                  return (
                    <div key={role.key} className="space-y-1">
                      <Label className="text-xs font-semibold flex items-center justify-between">
                        <span>{role.label}</span>
                        {slot.person_id && slot.phone && (
                          <Badge variant="outline" className="text-[10px] font-mono">
                            <Phone className="w-2.5 h-2.5 mr-1" />{slot.phone}
                          </Badge>
                        )}
                      </Label>
                      <select
                        value={slot.person_id ?? ""}
                        onChange={(e) => updateSlot(div.key, role.key, e.target.value)}
                        className="w-full h-9 px-2 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-rose-300 focus:border-rose-400 outline-none"
                        data-testid={`assign-${div.key}-${role.key}`}
                      >
                        <option value="">— Not assigned —</option>
                        {ensureIncluded(supervisorPool, slot.person_id).map(p => (
                          <option key={p.person_id} value={p.person_id} disabled={!p.phone}>
                            {p.name}{p.position_name ? ` · ${p.position_name}` : ""}{p.phone ? "" : " (no phone — not selectable)"}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="sticky bottom-2 flex justify-end gap-2 mt-4 p-3 rounded-xl bg-white border shadow-md">
        <Button
          onClick={handleSave}
          disabled={!dirty || saving}
          className="bg-rose-600 hover:bg-rose-700 text-white"
          data-testid="division-notifications-save"
        >
          <Save className="w-4 h-4 mr-1" />
          {saving ? "Saving..." : dirty ? "Save Assignments" : "Saved"}
        </Button>
      </div>

      {/* ── SPECIALTY RECIPIENTS — 2 people SMS'd when a specialist files an incident ── */}
      {!loading && (
        <Card className="border-emerald-200 bg-emerald-50/40 mt-6" data-testid="specialty-recipients-card">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Award className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm space-y-1 flex-1">
                <p className="font-semibold text-emerald-900">Specialist Incident Recipients</p>
                <p className="text-emerald-800">
                  When a <strong>Specialist</strong> files an incident report, these 2 people get an SMS. The Specialty supervisor login will also see the report on their dashboard. (Division supervisor of the kid's group is also notified — that's automatic.)
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {[0, 1].map((idx) => {
                const pid = specialistRecipients[idx];
                const person = pid ? findPoolPerson(pid) : null;
                return (
                  <div key={idx} className="space-y-1">
                    <Label className="text-xs font-semibold flex items-center justify-between">
                      <span>Recipient {idx + 1}</span>
                      {person && (
                        <span className="flex gap-1">
                          {person.phone && (
                            <Badge variant="outline" className="text-[10px] font-mono">
                              <Phone className="w-2.5 h-2.5 mr-1" />{person.phone}
                            </Badge>
                          )}
                          {person.email && (
                            <Badge variant="outline" className="text-[10px]">
                              <Mail className="w-2.5 h-2.5 mr-1" />{person.email.length > 20 ? person.email.slice(0, 20) + "…" : person.email}
                            </Badge>
                          )}
                        </span>
                      )}
                    </Label>
                    <select
                      value={pid ?? ""}
                      onChange={(e) => updateSpecialistSlot(idx, e.target.value)}
                      className="w-full h-9 px-2 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none"
                      data-testid={`specialist-recipient-${idx}`}
                    >
                      <option value="">— Not assigned —</option>
                      {ensureIncluded(supervisorPool, pid).map(p => (
                        <option key={p.person_id} value={p.person_id} disabled={!p.phone}>
                          {p.name}{p.position_name ? ` · ${p.position_name}` : ""}{p.phone ? "" : " (no phone)"}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={saveSpecialistRecipients}
                disabled={!specialistDirty || specialistSaving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="specialist-recipients-save"
              >
                <Save className="w-3.5 h-3.5 mr-1" />
                {specialistSaving ? "Saving..." : specialistDirty ? "Save Specialist Recipients" : "Saved"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── SKIPPERS GROUP LEADERS ── */}
      {!loading && (
        <Card className="border-amber-200 bg-amber-50/40 mt-4" data-testid="skippers-leaders-card">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <GraduationCap className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm space-y-1 flex-1">
                <p className="font-semibold text-amber-900">Skippers Group Leaders</p>
                <p className="text-amber-800">
                  When an incident is filed on a Skippers camper, the matching group leader below also gets an SMS (on top of the Skippers Supervisor + Assistant above). Pick from Skippers Supervisors/Group Leaders in CampMinder.
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {skipperLeaders.map((leader) => {
                const person = leader.person_id ? findPoolPerson(leader.person_id) : null;
                return (
                  <div key={leader.leader_role} className="space-y-1">
                    <Label className="text-xs font-semibold flex items-center justify-between">
                      <span>{leader.label}</span>
                      {person && (
                        <span className="flex gap-1">
                          {person.phone && (
                            <Badge variant="outline" className="text-[10px] font-mono">
                              <Phone className="w-2.5 h-2.5 mr-1" />{person.phone}
                            </Badge>
                          )}
                        </span>
                      )}
                    </Label>
                    <p className="text-[10px] text-amber-700/80">
                      Groups: {(leader.group_codes || []).join(", ")}
                    </p>
                    <select
                      value={leader.person_id ?? ""}
                      onChange={(e) => updateSkipperLeader(leader.leader_role, e.target.value)}
                      className="w-full h-9 px-2 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-amber-300 focus:border-amber-400 outline-none"
                      data-testid={`skipper-leader-${leader.leader_role}`}
                    >
                      <option value="">— Not assigned —</option>
                      {ensureIncluded(groupLeaderPool, leader.person_id).map(p => (
                        <option key={p.person_id} value={p.person_id} disabled={!p.phone}>
                          {p.name}{p.position_name ? ` · ${p.position_name}` : ""}{p.phone ? "" : " (no phone)"}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={saveSkipperLeaders}
                disabled={!skipperLeadersDirty || skipperLeadersSaving}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="skippers-leaders-save"
              >
                <Save className="w-3.5 h-3.5 mr-1" />
                {skipperLeadersSaving ? "Saving..." : skipperLeadersDirty ? "Save Group Leaders" : "Saved"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
